/**
 * The left tool rail (`KLQ-20260820-001` phase 5, issue #71; `chat1.md` wireframe 2, *Left tool
 * rail (84px)*).
 *
 * **It holds no state.** The armed tool, the colour and the two layer switches are
 * `ViewerPage`'s, passed in and reported out. The page is what decides that arming a tool stops
 * a press from placing a pin, that hiding Markup disarms a markup tool, and that leaving Comment
 * mode puts everything back — decisions about the whole screen, which a rail holding its own
 * copy of the tool would make a second time and eventually differently.
 *
 * **Pin is the first tool, and it is "no markup tool".** Wireframe 2 draws it first and active.
 * In this screen a press in Comment mode with nothing armed already places a pin, so the Pin
 * button is how a person gets back to that from Draw — not a sixth `MarkupKind`, which the wire
 * does not have.
 *
 * **Chrome, not content.** The rail frames the drawing, so it is ink like the stage beside it,
 * and an active tool is lit white rather than accent (the design's own comment on `Tool`):
 * accent on this screen means a comment pin, and a blue button beside blue pins would read as
 * one of them.
 *
 * **Eraser, Undo and Clear reach only this session's strokes** (owner, 2026-09-17). Every
 * stroke is still posted the moment the gesture ends; taking one back is `DELETE /markups/{id}`,
 * and what counts as *yours to take back* is `markupSession.ts`. The rail only says whether
 * there is anything to undo or clear — the page decides that, as it decides everything else.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Eraser,
  Eye,
  EyeOff,
  MapPin,
  Pencil,
  Ruler,
  Slash,
  Square,
  Trash2,
  Type,
  Undo2,
} from 'lucide-react';

import type { MessageKeys } from '@/lib/i18n';
import { useMessages } from '@/lib/i18n';

import { MARKUP_PALETTE, type MarkupTool } from './markup';
import styles from './Viewer.module.css';

type DrawingsKey = MessageKeys['drawings'];

/** One of the five offered colours, as the custom-property **name** — never its value. */
export type MarkupColor = (typeof MARKUP_PALETTE)[number];

/** What is drawn over the sheet. Rendering switches, never query keys (issue #71, step 5). */
export interface ViewerLayers {
  readonly pins: boolean;
  readonly markup: boolean;
  /** The task pins (`MCK-20260521-008`). A third layer, not a mode: a person reading a sheet
      wants the comments and the work owed on it at the same time. */
  readonly tasks: boolean;
}

export interface ToolRailProps {
  /** The armed markup tool, or `null` for Pin — a press places a pin. */
  readonly tool: MarkupTool | null;
  readonly color: MarkupColor;
  readonly layers: ViewerLayers;
  readonly onTool: (tool: MarkupTool | null) => void;
  readonly onColor: (color: MarkupColor) => void;
  readonly onLayers: (layers: ViewerLayers) => void;
  /** Whether this session has an act on the open version that Undo can take back. */
  readonly canUndo: boolean;
  /** Whether this session has strokes on the open version that Clear can take. */
  readonly canClear: boolean;
  readonly onUndo: () => void;
  readonly onClear: () => void;
}

interface ToolEntry {
  readonly tool: MarkupTool | null;
  readonly icon: LucideIcon;
  readonly label: DrawingsKey;
}

/** Wireframe 2's order: Pin, then the five markup kinds; the eraser after them. */
const TOOLS: readonly ToolEntry[] = [
  { tool: null, icon: MapPin, label: 'markup.tool.pin' },
  { tool: 'draw', icon: Pencil, label: 'markup.tool.draw' },
  { tool: 'line', icon: Slash, label: 'markup.tool.line' },
  { tool: 'box', icon: Square, label: 'markup.tool.box' },
  { tool: 'text', icon: Type, label: 'markup.tool.text' },
  { tool: 'measure', icon: Ruler, label: 'markup.tool.measure' },
  { tool: 'erase', icon: Eraser, label: 'markup.tool.erase' },
];

/**
 * A swatch's accessible name. TWIN: `MARKUP_PALETTE` in `markup.ts` — a colour added there
 * without a name here is a type error, which is the point of keying the record by the tuple.
 */
const COLOR_LABEL: Readonly<Record<MarkupColor, DrawingsKey>> = {
  '--danger-500': 'markup.color.red',
  '--accent-500': 'markup.color.blue',
  '--warning-500': 'markup.color.amber',
  '--success-500': 'markup.color.green',
  '--neutral-900': 'markup.color.black',
};

export function ToolRail({
  tool,
  color,
  layers,
  onTool,
  onColor,
  onLayers,
  canUndo,
  canClear,
  onUndo,
  onClear,
}: ToolRailProps) {
  const t = useMessages('drawings');

  const layerSwitch = (key: keyof ViewerLayers, label: DrawingsKey) => {
    const shown = layers[key];
    const Icon = shown ? Eye : EyeOff;
    return (
      <button
        type="button"
        className={[styles.tool, shown ? '' : styles.toolOff].filter(Boolean).join(' ')}
        aria-pressed={shown}
        onClick={() => {
          onLayers({ ...layers, [key]: !shown });
        }}
      >
        <Icon size={18} strokeWidth={1.5} aria-hidden />
        <span className={styles.toolLabel}>{t(label)}</span>
      </button>
    );
  };

  return (
    <div className={styles.toolRail}>
      <div className={styles.toolGroup} role="group" aria-label={t('markup.rail.label')}>
        {TOOLS.map((entry) => {
          const Icon = entry.icon;
          const active = entry.tool === tool;
          return (
            <button
              key={entry.label}
              type="button"
              className={[styles.tool, active ? styles.toolActive : ''].filter(Boolean).join(' ')}
              aria-pressed={active}
              onClick={() => {
                onTool(entry.tool);
              }}
            >
              <Icon size={18} strokeWidth={1.5} aria-hidden />
              <span className={styles.toolLabel}>{t(entry.label)}</span>
            </button>
          );
        })}
      </div>

      <div className={styles.toolDivider} />

      {/* Disabled rather than absent: they sit where they always sit, and a rail whose buttons
          come and go as strokes land would move the colour swatches under a finger. */}
      <div className={styles.toolGroup} role="group" aria-label={t('markup.history.label')}>
        <button type="button" className={styles.tool} disabled={!canUndo} onClick={onUndo}>
          <Undo2 size={18} strokeWidth={1.5} aria-hidden />
          <span className={styles.toolLabel}>{t('markup.undo')}</span>
        </button>
        <button type="button" className={styles.tool} disabled={!canClear} onClick={onClear}>
          <Trash2 size={18} strokeWidth={1.5} aria-hidden />
          <span className={styles.toolLabel}>{t('markup.clear')}</span>
        </button>
      </div>

      <div className={styles.toolDivider} />

      {/* Radio semantics on purpose: exactly one colour is always chosen, and arrow keys
          between swatches are what a screen reader user expects of a set like this. Native
          radios rather than `role="radio"` buttons, so that behaviour is the platform's. */}
      <fieldset className={styles.swatches}>
        <legend className={styles.toolHeading}>{t('markup.color.label')}</legend>
        {MARKUP_PALETTE.map((name) => (
          <label key={name} className={styles.swatch} title={t(COLOR_LABEL[name])}>
            <input
              type="radio"
              name="markup-color"
              className="sr-only"
              checked={name === color}
              onChange={() => {
                onColor(name);
              }}
            />
            {/* The token by name. `resolveColor` turns it into the wire's hex at the moment of
                a write, and nowhere else. */}
            <span
              className={styles.swatchChip}
              style={{ background: `var(${name})` }}
              aria-hidden
            />
            <span className="sr-only">{t(COLOR_LABEL[name])}</span>
          </label>
        ))}
      </fieldset>

      <div className={styles.toolDivider} />

      <div className={styles.toolGroup} role="group" aria-label={t('markup.layers.label')}>
        <span className={styles.toolHeading} aria-hidden>
          {t('markup.layers.label')}
        </span>
        {layerSwitch('pins', 'markup.layer.pins')}
        {layerSwitch('markup', 'markup.layer.markup')}
        {layerSwitch('tasks', 'markup.layer.tasks')}
      </div>
    </div>
  );
}
